import React from 'react';
import {Button, Table, Tag} from 'antd';
import type {TableColumnsType, TableProps} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {ReviewStatus, ReviewTaskListItem} from '../graphql/types/ReviewTaskType';
import {useReviewTaskCanOperate} from '../hooks/useReviewTaskPermission';

interface ReviewTaskTableProps {
    data: ReviewTaskListItem[];
    loading: boolean;
    onOpenDetail: (record: ReviewTaskListItem) => void;
    /** 选中的 reviewTaskId 列表（受控） */
    selectedKeys: number[];
    onSelectChange: (keys: number[]) => void;
    /** 当前提交时间列排序方向（受控）；null 表示后端默认（ASC/FIFO） */
    sortDirection?: 'ASC' | 'DESC' | null;
    /** 表头排序切换回调；点提交时间列在 ASC/DESC 间切换 */
    onSortChange?: (direction: 'ASC' | 'DESC') => void;
}

const STATUS_COLOR: Record<string, string> = {
    [ReviewStatus.PENDING_REVIEW]: 'orange',
    [ReviewStatus.APPROVED]: 'green',
    [ReviewStatus.REJECTED]: 'red',
    [ReviewStatus.CANCELLED]: 'default',
};

const ReviewTaskTable: React.FC<ReviewTaskTableProps> = ({
    data,
    loading,
    onOpenDetail,
    selectedKeys,
    onSelectChange,
    sortDirection,
    onSortChange,
}) => {
    const {t} = useTranslation();
    const canOperate = useReviewTaskCanOperate();

    // 提交时间列受控排序：ASC→ascend / DESC→descend / 其它→不高亮
    const submittedAtSortOrder: 'ascend' | 'descend' | undefined =
        sortDirection === 'ASC' ? 'ascend' : sortDirection === 'DESC' ? 'descend' : undefined;

    // 拦截表头排序：只处理提交时间列，转成 ASC/DESC 回调给上层重查（后端全量排序）
    const handleTableChange: TableProps<ReviewTaskListItem>['onChange'] = (
        _pagination,
        _filters,
        sorter
    ) => {
        if (!onSortChange) return;
        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const field = typeof single?.field === 'string' ? single.field : undefined;
        if (!field || field !== 'submittedAt' || !single?.order) {
            onSortChange('ASC');
        } else {
            onSortChange(single.order === 'ascend' ? 'ASC' : 'DESC');
        }
    };

    // 无审核操作权限时整列不渲染（而非渲染一列禁用勾选框）——批量审核入口对该用户不存在
    const rowSelection: TableProps<ReviewTaskListItem>['rowSelection'] | undefined = canOperate
        ? {
            selectedRowKeys: selectedKeys,
            onChange: (keys) => onSelectChange(keys as number[]),
            getCheckboxProps: (record) => ({
                // 仅待审核工单允许勾选进入批量队列；已审核/已取消不再处理
                disabled: record.status !== ReviewStatus.PENDING_REVIEW,
            }),
        }
        : undefined;

    const columns: TableColumnsType<ReviewTaskListItem> = [
        {
            title: t('reviewTask.table.reviewTaskId'),
            dataIndex: 'reviewTaskId',
            key: 'reviewTaskId',
            ellipsis: true,
        },
        {
            title: t('reviewTask.table.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            ellipsis: true,
            render: (value: string | undefined, record) =>
                value ? (
                    <Button type="link" size="small" onClick={() => onOpenDetail(record)}>
                        {value}
                    </Button>
                ) : (
                    '--'
                ),
        },
        {
            title: t('reviewTask.table.dspName'),
            dataIndex: 'dspName',
            key: 'dspName',
            ellipsis: true,
            render: (v?: string) => v ?? '--',
        },
        {
            title: t('reviewTask.table.submitterName'),
            dataIndex: 'submitterName',
            key: 'submitterName',
            ellipsis: true,
            render: (v?: string) => v ?? '--',
        },
        {
            title: t('reviewTask.table.phone'),
            dataIndex: 'submitterPhone',
            key: 'submitterPhone',
            ellipsis: true,
            render: (v?: string) => v ?? '--',
        },
        {
            title: t('reviewTask.table.status'),
            dataIndex: 'status',
            key: 'status',
            ellipsis: true,
            render: (v: string) => (
                <Tag color={STATUS_COLOR[v] ?? 'default'}>
                    {t(`reviewTask.status.${v}`)}
                </Tag>
            ),
        },
        {
            title: t('reviewTask.table.bizType'),
            dataIndex: 'bizType',
            key: 'bizType',
            ellipsis: true,
            render: (v: string) => (v ? t(`reviewTask.bizType.${v}`) : '--'),
        },
        {
            title: t('reviewTask.table.bizSubType'),
            dataIndex: 'bizSubType',
            key: 'bizSubType',
            ellipsis: true,
            render: (v?: string) =>
                v
                    ? (t(`customerComplaint.type.${v}`, {defaultValue: v}) as string)
                    : '--',
        },
        {
            title: t('reviewTask.table.submittedAt'),
            dataIndex: 'submittedAt',
            key: 'submittedAt',
            ellipsis: true,
            // 后端全量排序：点表头切 ASC/DESC，通过 onChange→onSortChange 重查
            sorter: true,
            sortOrder: submittedAtSortOrder,
            render: formatEpochToDateTime,
        },
        {
            title: t('reviewTask.table.reviewerId'),
            dataIndex: 'reviewerId',
            key: 'reviewerId',
            ellipsis: true,
            // CANCELLED（DSP 放弃申诉）无审核员介入，处理人按系统流转展示
            render: (_: unknown, record) =>
                record.status === ReviewStatus.CANCELLED
                    ? t('reviewTask.timeline.operator.system')
                    : record.reviewerName || record.reviewerId || '--',
        },
        {
            title: t('reviewTask.table.action'),
            key: 'action',
            fixed: 'right',
            width: 80,
            render: (_: unknown, record: ReviewTaskListItem) => (
                <Button type="link" size="small" onClick={() => onOpenDetail(record)}>
                    {record.status === ReviewStatus.PENDING_REVIEW
                        ? t('reviewTask.table.review')
                        : t('reviewTask.table.view')}
                </Button>
            ),
        },
    ];

    return (
        <Table
            rowKey="reviewTaskId"
            columns={columns}
            dataSource={data}
            loading={loading}
            rowSelection={rowSelection}
            pagination={false}
            onChange={handleTableChange}
            scroll={{x: 'max-content'}}
            size="middle"
            className="mb-4"
            tableLayout="auto"
        />
    );
};

export default ReviewTaskTable;
